// src/components/timeline/TimelineInfiniteList.tsx
"use client";

import { useState, useEffect } from "react";
import InfiniteList from "@/components/InfiniteList";
import { useTranslations } from "next-intl";
import type { Collection } from "@/types/timeline";
import type { PostReactionSummary, TimelinePost } from "@/lib/timeline";
import { getCollectionBySlugAction } from "@/app/actions/collection";
import TimelineCard from "./TimelineCard";

type Props = {
  projectId: number;
  initialItems: TimelinePost[];
  initialReactions: PostReactionSummary[];
  initialPage: number;
  initialHasMore: boolean;
  getNextPage: (page: number) => Promise<{
    items: TimelinePost[];
    hasMore: boolean;
    reactions: PostReactionSummary[];
  }>;
  collectionSlug?: string;
  mediaType?: string;
  tag?: string;
};

export default function TimelineInfiniteList({
  projectId,
  initialItems,
  initialReactions,
  initialPage,
  initialHasMore,
  getNextPage,
  collectionSlug,
  mediaType,
  tag,
}: Props) {
  const t = useTranslations();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [reactionMap, setReactionMap] = useState<
    Record<string, PostReactionSummary>
  >(() => {
    const map: Record<string, PostReactionSummary> = {};
    for (const reaction of initialReactions) {
      map[reaction.post_id] = reaction;
    }
    return map;
  });

  // Fetch collection only when collectionSlug is provided
  useEffect(() => {
    if (collectionSlug) {
      getCollectionBySlugAction(projectId, collectionSlug)
        .then(setCollection)
        .catch(console.error);
    }
  }, [projectId, collectionSlug]);

  // Priority: explicit tag > resolved collection title > raw slug > default
  const heading = tag
    ? `#${tag}`
    : collection
      ? `#${collection.title}`
      : collectionSlug
        ? `#${collectionSlug}`
        : t("nav.timeline");

  return (
    <InfiniteList<TimelinePost>
      initialItems={initialItems}
      initialPage={initialPage}
      initialHasMore={initialHasMore}
      getNextPage={async (page) => {
        const next = await getNextPage(page);
        setReactionMap((prev) => {
          const updated = { ...prev };
          for (const reaction of next.reactions) {
            updated[reaction.post_id] = reaction;
          }
          return updated;
        });
        return {
          items: next.items,
          hasMore: next.hasMore,
        };
      }}
    >
      {(items) => (
        <>
          <h1 style={{ marginBottom: "1rem", fontSize: "1.5rem" }}>
            {heading}
          </h1>

          {items.length === 0 ? (
            <div
              style={{
                padding: "3rem 1rem",
                textAlign: "center",
                opacity: 0.6,
              }}
            >
              {tag
                ? `No posts tagged #${tag} yet.`
                : collectionSlug
                  ? t("collection.no_posts", { collection: collectionSlug })
                  : t("collection.no_type_posts", {
                      type: t("nav.timeline").toLowerCase(),
                    })}
            </div>
          ) : (
            <div style={{ maxWidth: 720, margin: "1rem auto 0" }}>
              {items.map((post) => (
                <TimelineCard
                  key={post.id}
                  projectId={projectId}
                  post={post}
                  initialReactionSummary={reactionMap[post.id] ?? null}
                  collectionSlug={collectionSlug}
                  mediaType={mediaType}
                />
              ))}
            </div>
          )}
        </>
      )}
    </InfiniteList>
  );
}
